import type { ReactNode } from 'react'

// Declarative description of the "Choose your path" flowchart.
//
// Nothing here is positioned in absolute pixels: bubbles only declare which
// horizontal lane they sit in and which row they belong to. The engine
// (ChooseYourPathDiagram) measures the rendered bubbles + the real plan-box
// row in the DOM and assembles concrete coordinates from that, so the diagram
// reflows with the container/screen size instead of relying on tuned magic
// numbers that drift apart from the plan buttons below it.

export type DiagramAnchor = {
  /** Bubble the arrow attaches to. */
  bubbleId: string
  /** Horizontal attach point, as a percentage of the diagram width. */
  xPct: number
  /** Which edge of the bubble to attach to. */
  edge: 'top' | 'bottom'
}

/**
 * Which point of the label's own box pins to the arrow's label anchor, as
 * `${horizontal}-${vertical}`. The label hangs in the opposite direction:
 * 'right-center' pins the box's right edge at the anchor, so the box sits to
 * the LEFT of the arrow and is vertically centered on it. This is the only
 * label-placement knob — the box self-sizes its height from the wrapped text
 * (no character-count guessing), so a '*-center' anchor is always truly
 * centered regardless of how many lines the text wraps into.
 */
export type LabelAnchor =
  | 'left-top' | 'left-center' | 'left-bottom'
  | 'center-top' | 'center-center' | 'center-bottom'
  | 'right-top' | 'right-center' | 'right-bottom'

// Label fields shared by every arrow spec. A label always belongs to its
// arrow; these only control how its box is placed/sized relative to the
// arrow's anchor point, not which arrow it belongs to.
type DiagramArrowLabel = {
  label?: string
  /** Which corner/edge of the label box pins to the arrow's anchor. */
  labelAnchor?: LabelAnchor
  /** Label box width, as a percentage of the diagram width. Drives wrapping. */
  labelWidthPct?: number
  /**
   * Per-arrow stroke override; fields fall back to the config-level
   * arrowStyle. On a terminalBranch it styles the stem AND both legs —
   * override a single leg via legStrokes.
   */
  stroke?: DiagramArrowStyle
  /**
   * Plan indices (0-based plan-box order) whose HOVER emphasizes this arrow
   * with the config's hoverEmphasis stroke. terminalBranch legs emphasize by
   * their own planIndex automatically; this field covers upstream segments.
   */
  hoverPlanIndices?: number[]
}

export type DiagramArrowSpec =
  | (DiagramArrowLabel & {
      // Arrow between two bubbles.
      kind: 'link'
      from: DiagramAnchor
      to: DiagramAnchor
    })
  | (DiagramArrowLabel & {
      // Straight arrow from a bubble down to a single plan box.
      kind: 'terminal'
      from: DiagramAnchor
      planIndex: number
    })
  | (DiagramArrowLabel & {
      // Shared stem from a bubble that splits into two plan boxes.
      kind: 'terminalBranch'
      from: DiagramAnchor
      planIndices: [number, number]
      /** Per-leg stroke override, keyed by plan index; falls back to `stroke`. */
      legStrokes?: Partial<Record<number, DiagramArrowStyle>>
    })

export type DiagramBubble = {
  id: string
  /** 0-based row, top to bottom. Bubbles sharing a row are vertically aligned. */
  row: number
  /**
   * Horizontal anchor point, as a percentage of the diagram width. Which edge of
   * the bubble it pins is set by `anchor` (default 'center'): with 'right' the
   * bubble's RIGHT edge sits at xPct, so xPct: 100 keeps it flush with the
   * column's right edge at every width instead of drifting as px width changes;
   * 'left' pins the LEFT edge the same way (xPct: 0 = flush left).
   */
  xPct: number
  /** Which edge of the bubble `xPct` pins. Defaults to 'center'. */
  anchor?: 'center' | 'left' | 'right'
  /** Bubble width, as a percentage of the diagram width. */
  widthPct: number
  /**
   * Pixels shaved off the %-derived width (the anchored edge stays pinned),
   * so two 50% boxes can meet at an EXACT px gutter instead of a %-of-width
   * one that drifts with the column.
   */
  widthInsetPx?: number
  text: ReactNode
  textClassName?: string
}

export type ChooseYourPathDiagramConfig = {
  /** Chrome shared by every bubble box. */
  bubbleClassName: string
  bubbles: DiagramBubble[]
  arrows: DiagramArrowSpec[]
  /** Padding above the first row, in px. */
  topPad: number
  /**
   * Vertical gap below each row, in px. One entry per row; the final entry is
   * the space the terminal arrows drop through to reach the plan boxes.
   */
  rowGaps: number[]
  /** Fallback row heights (px) used until bubbles have been measured. */
  estimatedRowHeights: number[]
  /** How far a bent terminal arrow drops before its horizontal bend, in px. */
  branchStemLength: number
  /** How far above plan-box arrow tips the terminalBranch fan-out crossline sits, in px. */
  branchCrosslineGap: number
  /** Gap between the arrow tips and the plan boxes below, in px. */
  terminalGap: number
  /**
   * Clearance between every line end and the box edge it attaches to, in px.
   * 0 = lines flush with the boxes. Omitted = the stock 5.
   */
  arrowClearancePx?: number
  /** Tailwind classes for arrow label text. */
  arrowLabelClassName?: string
  /**
   * Stretch every bubble to its row's tallest measured height (text
   * vertically centered) so same-row boxes always match even when their
   * text wraps to different line counts.
   */
  equalRowHeights?: boolean
  /**
   * Hovering a plan box re-strokes the arrows on that plan's path (the specs'
   * hoverPlanIndices + the matching terminalBranch leg): the emphasis color,
   * and the arrow's own width plus the delta. Omitted = hover does nothing.
   */
  hoverEmphasis?: { color: string; strokeWidthDelta: number }
  /**
   * Arrow stroke treatment. Omitted = the stock solid 2.25px stroke with
   * 12px heads. `dasharray` applies to the shafts only (heads stay solid);
   * '0.1 5' with the round linecap reads as a fine dotted line.
   */
  arrowStyle?: DiagramArrowStyle
}

export type DiagramArrowStyle = {
  strokeWidth?: number
  dasharray?: string
  headSize?: number
  /** Stroke color for shafts AND heads; omitted = currentColor (full black). */
  color?: string
  /** Suppress every arrowhead — bare lines carry the flow top-to-bottom. */
  noHeads?: boolean
  /**
   * Apply `dasharray` to the arrowHEADS too (default keeps heads solid so
   * the tips stay crisp) — the fully-dotted treatment.
   */
  dottedHeads?: boolean
  /** Square line ends/joins (butt caps + miter joins) instead of rounded. */
  squareCaps?: boolean
  /**
   * Draw this arrow in top-to-bottom (stroke-dash reveal) once the diagram
   * scrolls into view, on top of a static underlay in the config-level
   * arrowStyle — so the path starts looking like every other line, then the
   * emphasized stroke sweeps in. Multiple drawIn arrows chain in y-order.
   * Straight segments only; honors prefers-reduced-motion (renders final
   * state immediately).
   */
  drawIn?: {
    /** Sweep speed. Default 280. */
    pxPerSecond?: number
    /** Extra delay before the first chained segment starts. Default 400. */
    initialDelayMs?: number
  }
}

export const DESKTOP_DIAGRAM_CONFIG: ChooseYourPathDiagramConfig = {
  bubbleClassName: 'absolute -translate-x-1/2 -translate-y-1/2 px-4 text-black/75 py-3 border-2 border-black/50 rounded-[10px]',
  topPad: 0,
  rowGaps: [54, 75, 85],
  estimatedRowHeights: [56, 104, 88],
  branchStemLength: 18,
  branchCrosslineGap: 18,
  terminalGap: 5,
  arrowLabelClassName: 'text-[18px] italic leading-[18px] px-2 text-balance',
  bubbles: [
    {
      id: 'race',
      row: 1,
      xPct: 50,
      widthPct: 85,
      text: (
        <div className="text-black/80">
          <b className="font-bold text-black">Race through the intelligence explosion</b> by
          having AIs self-improve and putting them in charge of more things (datacenters,
          factories, weapons) faster than China can?
        </div>
      ),
      textClassName: 'text-2xl leading-snug',
    },
    {
      id: 'slow-down',
      row: 2,
      xPct: 30,
      widthPct: 50,
      text: 'Slow down at least a bit to work on safety and governance?',
      textClassName: 'text-2xl leading-snug',
    },
    {
      id: 'make-a-deal',
      row: 2,
      xPct: 77,
      widthPct: 40,
      text: "Let’s make a deal with China. But what?",
      textClassName: 'text-2xl leading-snug',
    },
  ],
  arrows: [
    {
      kind: 'link',
      from: { bubbleId: 'race', xPct: 48, edge: 'bottom' },
      to: { bubbleId: 'slow-down', xPct: 48, edge: 'top' },
      label: '“Yes, and that’s good actually\n and/or we have no choice.”',
      labelAnchor: 'right-center',
      labelWidthPct: 48,
    },
    {
      kind: 'link',
      from: { bubbleId: 'race', xPct: 64, edge: 'bottom' },
      to: { bubbleId: 'make-a-deal', xPct: 70, edge: 'top' },
      label: '“What? No, that’s crazy!”',
      labelAnchor: 'left-center',
      labelWidthPct: 35,
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 12, edge: 'bottom' },
      planIndex: 0,
      label: '“Nope”',
      labelAnchor: 'right-center',
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 24, edge: 'bottom' },
      planIndex: 1,
      label: '“Yes”',
      labelAnchor: 'left-center',
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 40, edge: 'bottom' },
      planIndex: 2,
      label: '“Yes, and slow down China too.”',
      labelAnchor: 'left-center',
      labelWidthPct: 24,
    },
    {
      kind: 'terminalBranch',
      from: { bubbleId: 'make-a-deal', xPct: 64, edge: 'bottom' },
      planIndices: [3, 4],
      label: '(Two of many possible deals)',
      labelAnchor: 'left-center',
      labelWidthPct: 40,
    },
  ],
}

export const MOBILE_DIAGRAM_CONFIG: ChooseYourPathDiagramConfig = {
  bubbleClassName: 'absolute -translate-x-1/2 -translate-y-1/2 px-4 text-black/75 py-3 border-2 border-black/50 rounded-[10px]',
  topPad: 0,
  rowGaps: [54, 85, 100],
  estimatedRowHeights: [56, 104, 88],
  branchStemLength: 18,
  branchCrosslineGap: 18,
  terminalGap: 5,
  arrowLabelClassName: 'text-[18px] italic leading-[18px] px-1 text-balance',
  bubbles: [
    {
      id: 'race',
      row: 1,
      xPct: 50,
      widthPct: 85,
      text: (
        <div className="text-black/80">
          <b className="font-bold text-black">Race through the intelligence explosion</b> by
          having AIs self-improve and putting them in charge of more things (datacenters,
          factories, weapons) faster than China can?
        </div>
      ),
      textClassName: 'text-xl leading-snug',
    },
    {
      id: 'slow-down',
      row: 2,
      xPct: 29,
      widthPct: 52,
      text: 'Slow down at least a bit to work on safety and governance?',
      textClassName: 'text-xl leading-snug',
    },
    {
      id: 'make-a-deal',
      row: 2,
      xPct: 77,
      widthPct: 40,
      text: "Let’s make a deal with China. But what?",
      textClassName: 'text-xl  leading-snug',
    },
  ],
  arrows: [
    {
      kind: 'link',
      from: { bubbleId: 'race', xPct: 48, edge: 'bottom' },
      to: { bubbleId: 'slow-down', xPct: 48, edge: 'top' },
      label: '“Yes, and that’s good actually and/or we have no choice.”',
      labelAnchor: 'right-center',
      labelWidthPct: 50,
    },
    {
      kind: 'link',
      from: { bubbleId: 'race', xPct: 64, edge: 'bottom' },
      to: { bubbleId: 'make-a-deal', xPct: 70, edge: 'top' },
      label: '“What? No, that’s crazy!”',
      labelAnchor: 'left-center',
      labelWidthPct: 35,
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 7, edge: 'bottom' },
      planIndex: 0,
      label: '“No”',
      labelAnchor: 'left-center',
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 23, edge: 'bottom' },
      planIndex: 1,
      label: '“Yes”',
      labelAnchor: 'left-center',
    },
    {
      kind: 'terminal',
      from: { bubbleId: 'slow-down', xPct: 43, edge: 'bottom' },
      planIndex: 2,
      label: '“Yes, and slow China too.”',
      labelAnchor: 'left-center',
      labelWidthPct: 25,
    },
    {
      kind: 'terminalBranch',
      from: { bubbleId: 'make-a-deal', xPct: 77, edge: 'bottom' },
      planIndices: [3, 4],
      label: '(two deal possibilities)',
      labelAnchor: 'left-center',
      labelWidthPct: 25,
    },
  ],
}
